import { RefObject, useEffect, useRef } from 'react';

export interface ViewerKeyHandlers {
  /** Esc: a dialog inside the viewer closes first (design a3), then the viewer. */
  onEscape: () => void;
  onMove: (delta: -1 | 1) => void;
  onTogglePlay: () => void;
}

/** True when the key belongs to the element that has the focus (a field, the video controls). */
function ownsKeys(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLVideoElement
  );
}

/** True for an element that Space presses (a button, a link). */
function pressesSpace(target: EventTarget | null): boolean {
  return target instanceof HTMLButtonElement || target instanceof HTMLAnchorElement;
}

/**
 * The keys of the viewer (designs a1, a2): Esc closes, ← and → move, Space plays and pauses the
 * video while the focus is not on the video controls, a field or a button.
 */
export function useViewerKeys(ref: RefObject<HTMLElement | null>, handlers: ViewerKeyHandlers): void {
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    const root = ref.current;
    if (root === null) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        latest.current.onEscape();
        return;
      }
      if (ownsKeys(event.target) || event.altKey || event.metaKey || event.ctrlKey) {
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        latest.current.onMove(event.key === 'ArrowLeft' ? -1 : 1);
        return;
      }
      if (event.key === ' ' && !pressesSpace(event.target)) {
        event.preventDefault();
        latest.current.onTogglePlay();
      }
    };
    root.addEventListener('keydown', onKeyDown);
    return () => root.removeEventListener('keydown', onKeyDown);
  }, [ref]);
}
